import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { ClientId, daysAgo } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import { Header } from "components/header";
import { trpc } from "components/rpc/trpc";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback } from "react";
import { useFeature } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";

interface AiClientSummaryProps {
    clientId: ClientId;
}

export const AiSummary: React.FC<AiClientSummaryProps> = ({ clientId }) => {
    const enabled = useFeature("aiClientSummary");
    const trpcClient = trpc.useTRPC();

    const {
        data: summary,
        isLoading: isQueryLoading,
        error: queryError,
        refetch: refetchSummary,
    } = useQuery({
        ...trpcClient.clientAiSummary.find.queryOptions({ clientId }),
    });

    const generateSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.generate.mutationOptions(),
        onSuccess: () => {
            refetchSummary();
        },
    });

    const handleGenerateOrRegenerateSummary = useCallback(async () => {
        handleEvent("ai-summary-generate", {
            clientId,
            isRegenerate: !!summary,
        });
        generateSummaryMutation.mutate({
            clientId,
        });
    }, [clientId, generateSummaryMutation, summary]);

    const isLoading = isQueryLoading || generateSummaryMutation.isPending;
    const error = queryError?.message || generateSummaryMutation.error?.message;

    if (!enabled) {
        return null;
    }
    return (
        <InputFormBlock>
            <Header
                iconName="29_ClientSummary"
                action={
                    <SecondaryButton
                        disabled={isLoading}
                        onClick={handleGenerateOrRegenerateSummary}
                        className="flex items-center space-x-2">
                        {isLoading ? <Spinner /> : <ArrowPathIcon className="w-4 h-4" />}
                        {summary ? (
                            <span className="text-sm">Regenerate Summary</span>
                        ) : (
                            <span className="text-sm">Generate Summary</span>
                        )}
                    </SecondaryButton>
                }>
                AI Summary
            </Header>

            {error && (
                <div className="text-red-600 text-sm mb-4 p-3 bg-red-50 rounded-md">
                    {error}
                </div>
            )}

            {summary ? (
                <div className="space-y-2">
                    <div className="bg-white rounded-md">
                        <div className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                            {summary.text}
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                            Generated {daysAgo(summary.dateAdded)}
                        </p>
                    </div>
                </div>
            ) : (
                <p className="text-gray-600 text-sm italic">
                    Generate an AI-powered summary of this client&apos;s information,
                    notes, and communications.
                </p>
            )}
        </InputFormBlock>
    );
};
